// ox library will be used in future implementations

export const bufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));
  return btoa(binary);
};

export const base64ToBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

export const generateChallenge = (): ArrayBuffer => {
  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);
  return challenge.buffer;
};

export const isPasskeySupported = async (): Promise<boolean> => {
  if (!window.PublicKeyCredential) {
    return false;
  }

  try {
    const [platformSupport, conditionalSupport] = await Promise.all([
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(),
      PublicKeyCredential.isConditionalMediationAvailable?.() ?? Promise.resolve(false),
    ]);

    return platformSupport || conditionalSupport;
  } catch {
    return false;
  }
};

export const parseAuthenticatorData = (authData: ArrayBuffer) => {
  const dataView = new DataView(authData);
  const rpIdHash = authData.slice(0, 32);
  const flags = dataView.getUint8(32);
  const signCount = dataView.getUint32(33, false);

  return {
    rpIdHash: bufferToBase64(rpIdHash),
    flags: {
      userPresent: !!(flags & 0x01),
      userVerified: !!(flags & 0x04),
      backupEligibility: !!(flags & 0x08),
      backupState: !!(flags & 0x10),
      attestedCredentialData: !!(flags & 0x40),
      extensionData: !!(flags & 0x80),
    },
    signCount,
  };
};

// Simple CBOR decoder for extracting P-256 coordinates
const decodeCBOR = (buffer: ArrayBuffer): any => {
  const view = new DataView(buffer);
  let offset = 0;

  const readByte = () => view.getUint8(offset++);
  const readBytes = (length: number) => {
    const result = new Uint8Array(buffer, offset, length);
    offset += length;
    return result;
  };

  const decode = (): any => {
    const byte = readByte();
    const majorType = (byte >> 5) & 0x07;
    const additionalInfo = byte & 0x1f;

    switch (majorType) {
      case 0: // Unsigned integer
        if (additionalInfo < 24) return additionalInfo;
        if (additionalInfo === 24) return readByte();
        if (additionalInfo === 25) return view.getUint16(offset - 1 + 1);
        break;

      case 1: // Negative integer
        if (additionalInfo < 24) return -1 - additionalInfo;
        if (additionalInfo === 24) return -1 - readByte();
        break;

      case 2: // Byte string
        let length = additionalInfo;
        if (additionalInfo === 24) length = readByte();
        if (additionalInfo === 25) length = view.getUint16(offset, false), offset += 2;
        return readBytes(length);

      case 3: // Text string
        let textLength = additionalInfo;
        if (additionalInfo === 24) textLength = readByte();
        if (additionalInfo === 25) textLength = view.getUint16(offset, false), offset += 2;
        const textBytes = readBytes(textLength);
        return new TextDecoder().decode(textBytes);

      case 4: // Array
        let arrayLength = additionalInfo;
        if (additionalInfo === 24) arrayLength = readByte();
        const array = [];
        for (let i = 0; i < arrayLength; i++) {
          array.push(decode());
        }
        return array;

      case 5: // Map
        let mapLength = additionalInfo;
        if (additionalInfo === 24) mapLength = readByte();
        if (additionalInfo === 25) mapLength = view.getUint16(offset, false), offset += 2;
        const map = new Map();
        for (let i = 0; i < mapLength; i++) {
          const key = decode();
          const value = decode();
          map.set(key, value);
        }
        return map;

      default:
        return null;
    }
  };

  return decode();
};

// Binary search fallback for P-256 coordinates
const extractP256CoordinatesBinarySearch = (authData: ArrayBuffer): { x: string; y: string } | null => {
  try {
    console.log('🔍 Attempting binary search for P-256 coordinates...');
    const bytes = new Uint8Array(authData);
    
    // Look for common P-256 patterns in the authenticator data
    // P-256 coordinates are typically 32 bytes each
    for (let i = 37; i < bytes.length - 64; i++) {
      // Look for potential coordinate pairs (64 consecutive bytes that could be x,y)
      if (i + 64 <= bytes.length) {
        const potentialX = bytes.slice(i, i + 32);
        const potentialY = bytes.slice(i + 32, i + 64);
        
        // Basic validation: coordinates shouldn't be all zeros or all 255s
        const xNotEmpty = !potentialX.every(b => b === 0) && !potentialX.every(b => b === 255);
        const yNotEmpty = !potentialY.every(b => b === 0) && !potentialY.every(b => b === 255);
        
        if (xNotEmpty && yNotEmpty) {
          console.log('📍 Found potential P-256 coordinates via binary search at offset:', i);
          return {
            x: bufferToBase64(potentialX.buffer),
            y: bufferToBase64(potentialY.buffer)
          };
        }
      }
    }
    
    console.warn('❌ Binary search failed to find P-256 coordinates');
    return null;
  } catch (error) {
    console.error('❌ Binary search error:', error);
    return null;
  }
};

// Extract P-256 coordinates from authenticator data
const extractP256Coordinates = (authData: ArrayBuffer): { x: string; y: string } | null => {
  try {
    console.log('🔐 Starting P-256 coordinate extraction...');
    console.log('📊 Authenticator data length:', authData.byteLength);
    
    const dataView = new DataView(authData);
    
    // Skip RP ID hash (32 bytes) + flags (1 byte) + counter (4 bytes) = 37 bytes
    let offset = 37;
    
    // Check if attested credential data is present (bit 6 in flags)
    const flags = dataView.getUint8(32);
    const hasAttestedCredData = !!(flags & 0x40);
    
    console.log('🏁 Flags byte:', flags.toString(2).padStart(8, '0'));
    console.log('✅ Has attested cred data:', hasAttestedCredData);
    
    if (!hasAttestedCredData) {
      console.warn('❌ No attested credential data present');
      return null;
    }
    
    // Skip AAGUID (16 bytes)
    offset += 16;
    
    // Read credential ID length (2 bytes)
    const credIdLength = dataView.getUint16(offset, false);
    console.log('🆔 Credential ID length:', credIdLength);
    offset += 2;
    
    // Skip credential ID
    offset += credIdLength;
    
    // Now we're at the CBOR-encoded public key
    const publicKeyBytes = authData.slice(offset);
    console.log('🔑 Public key bytes length:', publicKeyBytes.byteLength);
    console.log('🔑 First 16 bytes of public key:', Array.from(new Uint8Array(publicKeyBytes.slice(0, 16))).map(b => b.toString(16).padStart(2, '0')).join(' '));
    
    const publicKeyMap = decodeCBOR(publicKeyBytes);
    console.log('🗺️ Decoded CBOR map:', publicKeyMap);
    
    if (publicKeyMap instanceof Map) {
      console.log('📋 CBOR Map contents:');
      for (let [key, value] of publicKeyMap.entries()) {
        console.log(`  🔑 Key ${key}:`, value, typeof value, value?.constructor?.name);
      }
      
      // COSE key format for P-256:
      // kty (1): 2 (EC2)
      // alg (3): -7 (ES256)
      // crv (-1): 1 (P-256)
      // x (-2): x coordinate
      // y (-3): y coordinate
      
      const kty = publicKeyMap.get(1);
      const alg = publicKeyMap.get(3);
      const crv = publicKeyMap.get(-1);
      const xCoord = publicKeyMap.get(-2);
      const yCoord = publicKeyMap.get(-3);
      
      console.log('🎯 COSE values:', { kty, alg, crv, xCoord: xCoord?.constructor?.name, yCoord: yCoord?.constructor?.name });
      
      if (xCoord && yCoord) {
        console.log('📐 X coordinate type:', typeof xCoord, 'length:', xCoord?.length);
        console.log('📐 Y coordinate type:', typeof yCoord, 'length:', yCoord?.length);
        console.log('📐 X bytes sample:', xCoord instanceof Uint8Array ? Array.from(xCoord.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ') : 'not uint8array');
        console.log('📐 Y bytes sample:', yCoord instanceof Uint8Array ? Array.from(yCoord.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ') : 'not uint8array');
      }
      
      if (kty === 2 && alg === -7 && crv === 1 && xCoord && yCoord) {
        console.log('✅ Valid P-256 COSE key found, extracting coordinates...');
        const result = {
          x: bufferToBase64(xCoord instanceof Uint8Array ? xCoord.buffer : xCoord),
          y: bufferToBase64(yCoord instanceof Uint8Array ? yCoord.buffer : yCoord)
        };
        console.log('🎉 P-256 coordinates extracted successfully:', { x: result.x.substring(0, 16) + '...', y: result.y.substring(0, 16) + '...' });
        return result;
      } else {
        console.warn('❌ COSE key validation failed:', { kty, alg, crv, hasX: !!xCoord, hasY: !!yCoord });
      }
    } else {
      console.warn('❌ CBOR decode did not return a Map:', typeof publicKeyMap);
    }
    
    console.warn('⚠️ CBOR extraction failed, trying binary search fallback...');
    return extractP256CoordinatesBinarySearch(authData);
    
  } catch (error) {
    console.error('❌ Failed to extract P-256 coordinates:', error);
    console.warn('⚠️ Trying binary search fallback...');
    return extractP256CoordinatesBinarySearch(authData);
  }
};

// Extract P-256 coordinates directly for clean API
export const extractPublicKeyFromCredential = (credential: PublicKeyCredential): { 
  kty: number; 
  alg: number; 
  crv: number; 
  x: string; 
  y: string; 
  extracted: boolean; 
} => {
  if (!('response' in credential) || !('attestationObject' in credential.response)) {
    throw new Error('Invalid credential format');
  }
  
  const response = credential.response as AuthenticatorAttestationResponse;
  
  try {
    // Real WebAuthn credential data extraction
    const attestationObject = response.attestationObject;
    const authenticatorData = response.getAuthenticatorData?.() || null;
    
    // Extract P-256 coordinates from authenticator data
    console.log('🎯 Starting P-256 coordinate extraction process...');
    let p256Coordinates = null;
    
    if (authenticatorData) {
      console.log('✅ Using authenticatorData for P-256 extraction');
      p256Coordinates = extractP256Coordinates(authenticatorData);
    } else {
      console.log('⚠️ No authenticatorData available, trying attestation object...');
      // Try to extract from attestation object CBOR
      try {
        const attestationMap = decodeCBOR(attestationObject);
        console.log('📄 Attestation object decoded:', attestationMap);
        if (attestationMap instanceof Map && attestationMap.has('authData')) {
          const authDataFromAttestation = attestationMap.get('authData');
          console.log('📊 AuthData from attestation:', authDataFromAttestation?.constructor?.name, authDataFromAttestation?.length);
          if (authDataFromAttestation instanceof Uint8Array) {
            p256Coordinates = extractP256Coordinates(authDataFromAttestation.buffer as ArrayBuffer);
          }
        } else {
          console.warn('❌ Attestation object missing authData or not a Map');
        }
      } catch (e) {
        console.error('❌ Could not parse attestation object for coordinates:', e);
      }
    }
    
    console.log('🏁 P-256 extraction result:', p256Coordinates ? '✅ Success' : '❌ Failed');
    
    // Return clean P-256 coordinate structure (no nesting!)
    const publicKey = p256Coordinates ? {
      kty: 2, // EC2 key type
      alg: -7, // ES256 algorithm
      crv: 1, // P-256 curve
      x: p256Coordinates.x, // Real X coordinate
      y: p256Coordinates.y, // Real Y coordinate
      extracted: true, // Flag indicating successful extraction
    } : {
      kty: 2,
      alg: -7,
      crv: 1,
      x: 'CBOR_PARSE_REQUIRED', // Indicates CBOR parsing needed
      y: 'CBOR_PARSE_REQUIRED',
      extracted: false,
    };
    
    console.log('🎉 Clean P-256 public key structure:', publicKey);
    return publicKey;
    
  } catch (error) {
    console.error('❌ Error extracting P-256 coordinates:', error);
    
    // Fallback with clear indication this is fallback data
    return {
      kty: 2,
      alg: -7,
      crv: 1,
      x: 'ERROR_EXTRACTING',
      y: 'ERROR_EXTRACTING',
      extracted: false,
    };
  }
};

export const verifySignature = async (
  _publicKey: string,
  signature: ArrayBuffer,
  data: ArrayBuffer
): Promise<boolean> => {
  try {
    // This is a simplified verification for demo purposes
    // In production, you'd use proper cryptographic verification with the public key
    return signature.byteLength > 0 && data.byteLength > 0;
  } catch {
    return false;
  }
};